import React, { FC, ReactNode } from "react";
import classNames from "classnames";
import {
  GlyphFilm,
  GlyphZap,
  GlyphCirclePlus,
  GlyphShield,
  GlyphImage,
  GlyphFileText,
  GlyphFolderDown,
} from "./glyphs";
import {
  DownloadType,
  FILE_TYPE_META,
  isDownloadTypeVisible,
} from "../../pages/admin/videos/selectedVideoCardV2/helpers";

/**
 * The grouped download list.
 *
 * What is offered is EXACTLY what the two menus offered before: a format the
 * brand has not enabled is not listed, and an unapproved video that only
 * qualifies for the watermarked cut lists that one row and nothing else. The
 * change here is grouping and wording, never the set of choices.
 */

interface Group {
  label: string;
  types: DownloadType[];
}

/**
 * Two groups plus a divided "All content".
 *
 * NOTE a real change: the enum's order puts All content FIRST and Watermarked
 * LAST. Grouping necessarily reorders — Watermarked joins the video formats and
 * All content moves to the bottom, as the design draws it. Same types, same
 * gates, different sequence.
 */
const GROUPS: Group[] = [
  {
    label: "Video",
    types: [DownloadType.HIGH_RES, DownloadType.WEB, DownloadType.WEBM, DownloadType.WATERMARK],
  },
  { label: "Assets", types: [DownloadType.THUMB, DownloadType.TRANSCRIPT] },
];

const GLYPH: Record<DownloadType, ReactNode> = {
  [DownloadType.HIGH_RES]: <GlyphFilm />,
  [DownloadType.WEB]: <GlyphZap />,
  [DownloadType.WEBM]: <GlyphCirclePlus />,
  [DownloadType.WATERMARK]: <GlyphShield />,
  [DownloadType.THUMB]: <GlyphImage />,
  [DownloadType.TRANSCRIPT]: <GlyphFileText />,
  [DownloadType.ALL]: <GlyphFolderDown />,
};

export interface DownloadMenuListProps {
  /** False when the whole menu is unusable — unapproved, or a download in flight. */
  canDownload: boolean;
  /** Unapproved + not a pre-approval downloader: watermarked is the only row served. */
  showOnlyWatermarked?: boolean;
  /** The brand's `enabled_file_types`, or null for "no restriction". */
  allowedTypes: Set<string> | null;
  onPick: (type: DownloadType) => void;
  /** Replaces the standing footer note while a download is being prepared. */
  footer?: ReactNode;
}

export const MenuRow: FC<{
  glyph: ReactNode;
  title: string;
  description: string;
  disabled: boolean;
  onClick: () => void;
}> = ({ glyph, title, description, disabled, onClick }) => (
  <button
    type="button"
    role="menuitem"
    disabled={disabled}
    aria-disabled={disabled}
    onClick={() => !disabled && onClick()}
    className={classNames(
      "w-full flex items-start gap-[11px] text-left rounded-lg transition-colors",
      disabled ? "cursor-not-allowed" : "cursor-pointer hover:bg-tint",
    )}
    style={{ padding: "9px 10px", opacity: disabled ? 0.5 : 1 }}
  >
    <span className="text-accent-text shrink-0" style={{ marginTop: 1 }}>
      {glyph}
    </span>
    <span className="flex-1 min-w-0">
      <span className="block text-text-1" style={{ fontSize: 13.5, lineHeight: 1.2 }}>
        {title}
      </span>
      <span className="block text-text-2" style={{ fontSize: 11, lineHeight: 1.35, marginTop: 3 }}>
        {description}
      </span>
    </span>
  </button>
);

const Row: FC<{
  type: DownloadType;
  disabled: boolean;
  onPick: (t: DownloadType) => void;
}> = ({ type, disabled, onPick }) => {
  const meta = FILE_TYPE_META[type];
  return (
    <MenuRow
      glyph={GLYPH[type]}
      title={meta.title}
      description={meta.description}
      disabled={disabled}
      onClick={() => onPick(type)}
    />
  );
};

export const DownloadMenuList: FC<DownloadMenuListProps> = ({
  canDownload,
  showOnlyWatermarked = false,
  allowedTypes,
  onPick,
  footer,
}) => {
  /**
   * The one gate, lifted verbatim from both old menus:
   *
   *   showOnlyWatermarked ? [WATERMARK] : filter(isDownloadTypeVisible)
   *
   * so a type the brand has switched off is absent rather than dimmed, and the
   * watermark-only case offers no other row — All content included, since there
   * is no approved content to bundle.
   */
  const isOffered = (type: DownloadType) =>
    showOnlyWatermarked ? type === DownloadType.WATERMARK : isDownloadTypeVisible(type, allowedTypes);

  const row = (type: DownloadType) => (
    <Row key={type} type={type} disabled={!canDownload} onPick={onPick} />
  );

  // Groups are resolved before rendering so an emptied one takes its heading
  // and its divider with it.
  const groups = GROUPS.map((group) => ({
    label: group.label,
    types: group.types.filter(isOffered),
  })).filter((group) => group.types.length > 0);

  const offersAll = isOffered(DownloadType.ALL);

  return (
    <div role="menu" aria-label="Download formats">
      {groups.map((group, i) => (
        <div key={group.label} className={classNames(i > 0 && "border-t border-border-token")}>
          <div
            className="uppercase text-muted"
            style={{
              fontSize: 9.5,
              letterSpacing: ".12em",
              padding: "12px 14px 8px",
              // font-[…] is the arbitrary-value form. The bare
              // `font-CircularStdBlack` class emits NO CSS — tailwind.config.js
              // has no fontFamily key — so it silently renders body weight.
              fontFamily: "CircularStdBlack",
            }}
          >
            {group.label}
          </div>
          <div className="flex flex-col gap-0.5" style={{ padding: "0 8px 6px" }}>
            {group.types.map(row)}
          </div>
        </div>
      ))}

      {/* All content is divided off — it is every row above at once, not a
          seventh sibling. */}
      {offersAll && (
        <div
          className={classNames(groups.length > 0 && "border-t border-border-token")}
          style={{ padding: 8 }}
        >
          {row(DownloadType.ALL)}
        </div>
      )}

      {groups.length === 0 && !offersAll && (
        <div className="text-text-2" style={{ padding: "14px", fontSize: 12 }}>
          No download formats are available for this video.
        </div>
      )}

      <div
        className="border-t border-border-token bg-surface-2 text-text-2"
        style={{ padding: "10px 14px", fontSize: 11, lineHeight: 1.4 }}
      >
        {footer ?? "Each download opens the licensing terms before the file is prepared."}
      </div>
    </div>
  );
};

export default DownloadMenuList;
